import React, { useEffect, useMemo, useState } from "react";
import { product, Order } from "../../types";
import { Category } from "../../interfaces/product";
import Sidebar from "../../components/Sidebar";
import Loading from "../../components/Loading";
import { useAuth } from "../../context/AuthContext";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
} from "chart.js";

import { Doughnut, Line } from "react-chartjs-2";

import {
  getDashboardData,
} from "../../graphql/api/userApi";

import { fetchCategoriesAndSubcategories } from "../../graphql/api/categoryService";

// import "./Dashboard.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

const VendorDashboard: React.FC = () => {
  const { currentUser } = useAuth();

  const [products, setProducts] = useState<product[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [dateRange] = useState("Last 30 Days");

  // ---------------------------------------------------------
  // LOAD DATA
  // ---------------------------------------------------------

  const loadData = async () => {
    if (!currentUser?.id) {
      console.warn("No logged-in vendor found");
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const [
        dashboardData,
        { categories: categoriesArray },
      ] = await Promise.all([
        getDashboardData(currentUser.id),
        fetchCategoriesAndSubcategories(),
      ]);

      setProducts(dashboardData?.products || []);
      setOrders(dashboardData?.orders || []);
      setCategories(categoriesArray || []);
    } catch (error) {
      console.error(
        "Failed to load vendor dashboard data:",
        error
      );

      setProducts([]);
      setOrders([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [currentUser?.id]);

  // ---------------------------------------------------------
  // LOOKUP MAPS
  // ---------------------------------------------------------

  const productIdToDetailsMap = useMemo(() => {
    const map: { [key: string]: product } = {};

    products.forEach((item) => {
      map[item.id] = item;
    });

    return map;
  }, [products]);

  const categoryIdToNameMap = useMemo(() => {
    const map: { [key: string]: string } = {};

    categories.forEach((category) => {
      map[category.id] = category.name;
    });

    return map;
  }, [categories]);

  // ---------------------------------------------------------
  // ORDER STATUS
  // ---------------------------------------------------------

  const pendingOrders = useMemo(
    () =>
      orders.filter(
        (order) =>
          order.status?.toLowerCase() === "pending"
      ),
    [orders]
  );

  const shippedOrders = useMemo(
    () =>
      orders.filter(
        (order) =>
          order.status?.toLowerCase() === "shipped"
      ),
    [orders]
  );

  const deliveredOrders = useMemo(
    () =>
      orders.filter(
        (order) =>
          order.status?.toLowerCase() === "delivered"
      ),
    [orders]
  );

  const cancelledOrders = useMemo(
    () =>
      orders.filter(
        (order) =>
          order.status?.toLowerCase() === "cancelled"
      ),
    [orders]
  );

  // ---------------------------------------------------------
  // PRODUCT SALES
  // ---------------------------------------------------------

  const productSales = useMemo(() => {
    const sales: {
      [key: string]: number;
    } = {};

    orders.forEach((order) => {
      order.items?.forEach((item) => {
        sales[item.productId] =
          (sales[item.productId] || 0) +
          Number(item.quantity || 0);
      });
    });

    return sales;
  }, [orders]);

  // ---------------------------------------------------------
  // REVENUE
  // ---------------------------------------------------------

  /*
   * Dashboard data is already filtered to this vendor.
   *
   * If your OrderItem contains a vendor-specific price,
   * this calculation can later be changed to use:
   *
   * quantity × item.price
   *
   * For now it uses the order total because that field
   * already exists in your current Order type.
   */

  const totalRevenue = useMemo(() => {
    return orders.reduce(
      (total, order) =>
        total + Number(order.totalAmount || 0),
      0
    );
  }, [orders]);

  const averageOrderValue = useMemo(() => {
    return orders.length > 0
      ? totalRevenue / orders.length
      : 0;
  }, [orders, totalRevenue]);

  // ---------------------------------------------------------
  // CATEGORY SALES
  // ---------------------------------------------------------

  const categoryCounts = useMemo(() => {
    const result: {
      [key: string]: number;
    } = {};

    orders.forEach((order) => {
      order.items?.forEach((item) => {
        const productData =
          productIdToDetailsMap[item.productId];

        if (!productData) {
          return;
        }

        const categoryName =
          categoryIdToNameMap[
            productData.categoryId
          ] || "Uncategorized";

        result[categoryName] =
          (result[categoryName] || 0) +
          Number(item.quantity || 0);
      });
    });

    return result;
  }, [
    orders,
    productIdToDetailsMap,
    categoryIdToNameMap,
  ]);

  // ---------------------------------------------------------
  // DAILY REVENUE
  // ---------------------------------------------------------

  const groupedRevenue = useMemo(() => {
    const result: {
      [date: string]: number;
    } = {};

    orders.forEach((order) => {
      const date = new Date(
        order.createdAt
      ).toLocaleDateString("en-GB");

      result[date] =
        (result[date] || 0) +
        Number(order.totalAmount || 0);
    });

    return result;
  }, [orders]);

  // ---------------------------------------------------------
  // TOP PRODUCTS
  // ---------------------------------------------------------

  const topProducts = useMemo(() => {
    return [...products]
      .sort(
        (a, b) =>
          (productSales[b.id] || 0) -
          (productSales[a.id] || 0)
      )
      .slice(0, 5);
  }, [products, productSales]);

  // ---------------------------------------------------------
  // RECENT ORDERS
  // ---------------------------------------------------------

  const recentOrders = useMemo(() => {
    return [...orders]
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() -
          new Date(a.createdAt).getTime()
      )
      .slice(0, 6);
  }, [orders]);

  // ---------------------------------------------------------
  // CHART - REVENUE
  // ---------------------------------------------------------

  const revenueChartData = {
    labels: Object.keys(groupedRevenue),

    datasets: [
      {
        label: "Revenue",

        data: Object.values(
          groupedRevenue
        ),

        backgroundColor:
          "rgba(37, 99, 235, 0.15)",

        borderColor: "#2563EB",

        borderWidth: 2,

        fill: true,

        tension: 0.4,

        pointRadius: 3,
      },
    ],
  };

  const revenueChartOptions = {
    responsive: true,

    maintainAspectRatio: false,

    plugins: {
      legend: {
        display: false,
      },
    },

    scales: {
      y: {
        beginAtZero: true,

        grid: {
          color: "#E5E7EB",
        },

        ticks: {
          callback: (
            value: string | number
          ) => `₹${value}`,
        },
      },

      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  // ---------------------------------------------------------
  // CHART - CATEGORY
  // ---------------------------------------------------------

  const categoryChartData = {
    labels: Object.keys(
      categoryCounts
    ),

    datasets: [
      {
        data: Object.values(
          categoryCounts
        ),

        backgroundColor: [
          "#2563EB",
          "#10B981",
          "#F59E0B",
          "#EF4444",
          "#8B5CF6",
          "#06B6D4",
          "#EC4899",
          "#F97316",
        ],

        borderWidth: 0,
      },
    ],
  };

  const categoryChartOptions = {
    responsive: true,

    maintainAspectRatio: false,

    cutout: "68%",

    plugins: {
      legend: {
        position: "bottom" as const,

        labels: {
          usePointStyle: true,

          padding: 15,
        },
      },
    },
  };

  // ---------------------------------------------------------
  // CHART - ORDER STATUS
  // ---------------------------------------------------------

  const orderStatusData = {
    labels: [
      "Pending",
      "Shipped",
      "Delivered",
      "Cancelled",
    ],

    datasets: [
      {
        data: [
          pendingOrders.length,
          shippedOrders.length,
          deliveredOrders.length,
          cancelledOrders.length,
        ],

        backgroundColor: [
          "#F59E0B",
          "#3B82F6",
          "#10B981",
          "#EF4444",
        ],

        borderWidth: 0,
      },
    ],
  };

  // ---------------------------------------------------------
  // RETURN
  // ---------------------------------------------------------

  return (
    <div className="admin-dashboard">

      <Sidebar
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
      />

      <main
        className={`dashboard-main ${
          isSidebarOpen
            ? "sidebar-expanded"
            : "sidebar-collapsed"
        }`}
      >

        {/* =================================================
            PAGE HEADER
        ================================================= */}

        <section className="page-header">

          <div>

            <h1>
              Vendor Dashboard
            </h1>

            <p>
              Overview of your products,
              orders and sales performance.
            </p>

          </div>

          <div className="header-actions">

            <button
              className="refresh-button"
              onClick={loadData}
            >
              ↻ Refresh
            </button>

          </div>

        </section>

        {/* =================================================
            LOADING
        ================================================= */}

        {loading ? (

          <div className="dashboard-loading">
            <Loading />
          </div>

        ) : (

          <>

            {/* =================================================
                VENDOR KPI
            ================================================= */}

            <section className="section">

              <div className="kpi-grid">

                {/* PRODUCTS */}

                <div className="kpi-card purple">

                  <div className="kpi-icon">
                    📦
                  </div>

                  <div>

                    <span>
                      My Products
                    </span>

                    <strong>
                      {products.length}
                    </strong>

                    <small>
                      Products listed
                    </small>

                  </div>

                </div>

                {/* ORDERS */}

                <div className="kpi-card blue">

                  <div className="kpi-icon">
                    🛒
                  </div>

                  <div>

                    <span>
                      My Orders
                    </span>

                    <strong>
                      {orders.length}
                    </strong>

                    <small>
                      Customer orders
                    </small>

                  </div>

                </div>

                {/* PENDING */}

                <div className="kpi-card orange">

                  <div className="kpi-icon">
                    ⏳
                  </div>

                  <div>

                    <span>
                      Pending Orders
                    </span>

                    <strong>
                      {pendingOrders.length}
                    </strong>

                    <small>
                      Need attention
                    </small>

                  </div>

                </div>

                {/* REVENUE */}

                <div className="kpi-card emerald">

                  <div className="kpi-icon">
                    ₹
                  </div>

                  <div>

                    <span>
                      My Revenue
                    </span>

                    <strong>
                      ₹
                      {totalRevenue.toLocaleString(
                        "en-IN",
                        {
                          maximumFractionDigits: 0,
                        }
                      )}
                    </strong>

                    <small>
                      Avg ₹
                      {averageOrderValue.toFixed(
                        0
                      )}{" "}
                      / order
                    </small>

                  </div>

                </div>

              </div>

            </section>

            {/* =================================================
                REVENUE + CATEGORY
            ================================================= */}

            <section className="main-charts-grid">

              {/* REVENUE */}

              <div className="dashboard-card revenue-card">

                <div className="card-header">

                  <div>

                    <h3>
                      Revenue Overview
                    </h3>

                    <p>
                      Your revenue by order date
                    </p>

                  </div>

                  <span className="chart-badge">
                    {dateRange}
                  </span>

                </div>

                <div className="large-chart">

                  {Object.keys(
                    groupedRevenue
                  ).length > 0 ? (

                    <Line
                      data={
                        revenueChartData
                      }
                      options={
                        revenueChartOptions
                      }
                    />

                  ) : (

                    <div className="empty-chart">
                      No revenue data available
                    </div>

                  )}

                </div>

              </div>

              {/* CATEGORY */}

              <div className="dashboard-card">

                <div className="card-header">

                  <div>

                    <h3>
                      Sales by Category
                    </h3>

                    <p>
                      Your products sold by category
                    </p>

                  </div>

                </div>

                <div className="medium-chart">

                  {Object.keys(
                    categoryCounts
                  ).length > 0 ? (

                    <Doughnut
                      data={
                        categoryChartData
                      }
                      options={
                        categoryChartOptions
                      }
                    />

                  ) : (

                    <div className="empty-chart">
                      No category sales
                    </div>

                  )}

                </div>

              </div>

            </section>

            {/* =================================================
                ORDER BUSINESS
            ================================================= */}

            <section className="section">

              <div className="section-title-row">

                <div>

                  <h2>
                    Order Business
                  </h2>

                  <p>
                    Track the current status
                    of your customer orders
                  </p>

                </div>

              </div>

              <div className="order-summary-grid">

                <div className="order-status-card pending">

                  <span>
                    Pending
                  </span>

                  <strong>
                    {pendingOrders.length}
                  </strong>

                  <small>
                    Need attention
                  </small>

                </div>

                <div className="order-status-card shipped">

                  <span>
                    Shipped
                  </span>

                  <strong>
                    {shippedOrders.length}
                  </strong>

                  <small>
                    On the way
                  </small>

                </div>

                <div className="order-status-card delivered">

                  <span>
                    Delivered
                  </span>

                  <strong>
                    {deliveredOrders.length}
                  </strong>

                  <small>
                    Completed
                  </small>

                </div>

                <div className="order-status-card cancelled">

                  <span>
                    Cancelled
                  </span>

                  <strong>
                    {cancelledOrders.length}
                  </strong>

                  <small>
                    Cancelled orders
                  </small>

                </div>

              </div>

            </section>

            {/* =================================================
                PRODUCTS + ORDERS
            ================================================= */}

            <section className="three-column-grid">

              {/* =================================================
                  TOP PRODUCTS
              ================================================= */}

              <div className="dashboard-card">

                <div className="card-header">

                  <div>

                    <h3>
                      Top Products
                    </h3>

                    <p>
                      Your best selling products
                    </p>

                  </div>

                  <span className="view-link">
                    View All
                  </span>

                </div>

                <div className="top-products-list">

                  {topProducts.length === 0 ? (

                    <div className="empty-state">
                      No product sales yet
                    </div>

                  ) : (

                    topProducts.map(
                      (item, index) => (

                        <div
                          className="top-product-row"
                          key={item.id}
                        >

                          <div className="rank">
                            {index + 1}
                          </div>

                          <div className="product-placeholder">
                            📦
                          </div>

                          <div className="product-row-info">

                            <strong>
                              {item.name}
                            </strong>

                            <span>
                              {productSales[
                                item.id
                              ] || 0}{" "}
                              units sold
                            </span>

                          </div>

                          <strong className="product-price">

                            ₹
                            {Number(
                              item.price || 0
                            ).toLocaleString(
                              "en-IN"
                            )}

                          </strong>

                        </div>

                      )
                    )

                  )}

                </div>

              </div>

              {/* =================================================
                  RECENT ORDERS
              ================================================= */}

              <div className="dashboard-card">

                <div className="card-header">

                  <div>

                    <h3>
                      Recent Orders
                    </h3>

                    <p>
                      Latest customer orders
                    </p>

                  </div>

                  <span className="view-link">
                    View All
                  </span>

                </div>

                <div className="recent-list">

                  {recentOrders.length === 0 ? (

                    <div className="empty-state">
                      No orders yet
                    </div>

                  ) : (

                    recentOrders.map(
                      (order) => {

                        const productName =
                          order.items?.length >
                          0
                            ? productIdToDetailsMap[
                                order.items[0]
                                  .productId
                              ]?.name ||
                              "Product"
                            : "No products";

                        return (

                          <div
                            className="recent-order-row"
                            key={String(
                              order.id
                            )}
                          >

                            <div className="order-avatar">

                              {String(
                                order.id
                              )
                                .slice(0, 2)
                                .toUpperCase()}

                            </div>

                            <div className="recent-order-info">

                              <strong>

                                #
                                {String(
                                  order.id
                                ).slice(
                                  0,
                                  8
                                )}

                              </strong>

                              <span>
                                Customer Order
                              </span>

                              <small>
                                {productName}
                              </small>

                            </div>

                            <div className="recent-order-right">

                              <strong>

                                ₹
                                {Number(
                                  order.totalAmount ||
                                    0
                                ).toLocaleString(
                                  "en-IN"
                                )}

                              </strong>

                              <span
                                className={`status-badge ${order.status
                                  ?.toLowerCase()
                                  .replace(
                                    /\s+/g,
                                    "-"
                                  )}`}
                              >
                                {order.status}
                              </span>

                            </div>

                          </div>

                        );

                      }
                    )

                  )}

                </div>

              </div>

              {/* =================================================
                  INVENTORY SUMMARY
              ================================================= */}

              <div className="dashboard-card">

                <div className="card-header">

                  <div>

                    <h3>
                      Inventory Summary
                    </h3>

                    <p>
                      Overview of your products
                    </p>

                  </div>

                </div>

                <div className="summary-list">

                  <div>

                    <span>
                      Total Products
                    </span>

                    <strong>
                      {products.length}
                    </strong>

                  </div>

                  <div>

                    <span>
                      In Stock
                    </span>

                    <strong className="success-text">

                      {
                        products.filter(
                          (product: any) =>
                            Number(
                              product.stockQuantity ||
                                0
                            ) > 0
                        ).length
                      }

                    </strong>

                  </div>

                  <div>

                    <span>
                      Out of Stock
                    </span>

                    <strong className="warning-text">

                      {
                        products.filter(
                          (product: any) =>
                            Number(
                              product.stockQuantity ||
                                0
                            ) <= 0
                        ).length
                      }

                    </strong>

                  </div>

                  <div>

                    <span>
                      Total Orders
                    </span>

                    <strong>
                      {orders.length}
                    </strong>

                  </div>

                  <div>

                    <span>
                      Pending Orders
                    </span>

                    <strong className="warning-text">
                      {pendingOrders.length}
                    </strong>

                  </div>

                  <div>

                    <span>
                      Delivered Orders
                    </span>

                    <strong className="success-text">
                      {deliveredOrders.length}
                    </strong>

                  </div>

                  <div>

                    <span>
                      Cancelled Orders
                    </span>

                    <strong className="warning-text">
                      {cancelledOrders.length}
                    </strong>

                  </div>

                  <div>

                    <span>
                      Revenue
                    </span>

                    <strong>

                      ₹
                      {totalRevenue.toLocaleString(
                        "en-IN",
                        {
                          maximumFractionDigits: 0,
                        }
                      )}

                    </strong>

                  </div>

                </div>

              </div>

            </section>

            {/* =================================================
                ORDER STATUS + PERFORMANCE
            ================================================= */}

            <section className="bottom-grid">

              {/* ORDER STATUS */}

              <div className="dashboard-card">

                <div className="card-header">

                  <div>

                    <h3>
                      Order Status Distribution
                    </h3>

                    <p>
                      Your current order lifecycle
                    </p>

                  </div>

                </div>

                <div className="status-chart">

                  {orders.length > 0 ? (

                    <Doughnut
                      data={
                        orderStatusData
                      }
                      options={{
                        responsive: true,

                        maintainAspectRatio:
                          false,

                        cutout: "70%",

                        plugins: {
                          legend: {
                            position:
                              "bottom",
                          },
                        },
                      }}
                    />

                  ) : (

                    <div className="empty-chart">
                      No order data
                    </div>

                  )}

                </div>

              </div>

              {/* PERFORMANCE */}

              <div className="dashboard-card business-summary-card">

                <div className="card-header">

                  <div>

                    <h3>
                      Vendor Performance
                    </h3>

                    <p>
                      Important numbers for your store
                    </p>

                  </div>

                </div>

                <div className="summary-list">

                  <div>

                    <span>
                      Products
                    </span>

                    <strong>
                      {products.length}
                    </strong>

                  </div>

                  <div>

                    <span>
                      Orders
                    </span>

                    <strong>
                      {orders.length}
                    </strong>

                  </div>

                  <div>

                    <span>
                      Revenue
                    </span>

                    <strong>

                      ₹
                      {totalRevenue.toLocaleString(
                        "en-IN",
                        {
                          maximumFractionDigits: 0,
                        }
                      )}

                    </strong>

                  </div>

                  <div>

                    <span>
                      Average Order Value
                    </span>

                    <strong>

                      ₹
                      {averageOrderValue.toLocaleString(
                        "en-IN",
                        {
                          maximumFractionDigits: 0,
                        }
                      )}

                    </strong>

                  </div>

                  <div>

                    <span>
                      Pending
                    </span>

                    <strong className="warning-text">
                      {pendingOrders.length}
                    </strong>

                  </div>

                  <div>

                    <span>
                      Shipped
                    </span>

                    <strong>
                      {shippedOrders.length}
                    </strong>

                  </div>

                  <div>

                    <span>
                      Delivered
                    </span>

                    <strong className="success-text">
                      {deliveredOrders.length}
                    </strong>

                  </div>

                  <div>

                    <span>
                      Cancelled
                    </span>

                    <strong className="warning-text">
                      {cancelledOrders.length}
                    </strong>

                  </div>

                </div>

              </div>

            </section>

          </>

        )}

      </main>

    </div>
  );
};

export default VendorDashboard;